import React, { useState, useMemo } from 'react';
import { t } from '../../utils/translations';

const ConversationsTab = ({
  conversations,
  loadingConversations,
  currentConversation,
  onSelectConversation,
  onStartNewConversation,
  setActiveTab,
  userLanguage
}) => {
  const [activeTab, setActiveTabLocal] = useState('all');

  const localeMap = {
    en: 'en-US',
    sr: 'sr-RS',
    es: 'es-ES',
    fr: 'fr-FR',
    de: 'de-DE',
    it: 'it-IT',
    pt: 'pt-PT',
    ru: 'ru-RU',
    zh: 'zh-CN',
    ja: 'ja-JP',
    ko: 'ko-KR',
    ar: 'ar-SA',
    hi: 'hi-IN',
  };
  const dateLocale = localeMap[userLanguage] || 'en-US';
  
  const conversationTypes = [
    { key: 'all', label: t('convoFilterAll', userLanguage), icon: '💬' },
    { key: 'general', label: t('convoFilterGeneral', userLanguage), icon: '💭' },
    { key: 'subject_learning', label: t('convoFilterSubjectLearning', userLanguage), icon: '📚' },
    { key: 'section_learning', label: t('convoFilterSectionLearning', userLanguage), icon: '📖' },
    { key: 'quiz', label: t('convoFilterQuiz', userLanguage), icon: '🎮' },
    { key: 'today_section', label: t('convoFilterTodaySection', userLanguage), icon: '📝' }
  ];

  const filteredConversations = useMemo(() => {
    if (activeTab === 'all') {
      return conversations;
    }
    return conversations.filter(conv => conv.conversation_type === activeTab);
  }, [conversations, activeTab]);

  return (
    <div className="conversations-tab rulee-tab-card">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '1.5rem'
      }}>
        <h2>{t('myConversations', userLanguage)}</h2>
        <button
          className="btn-secondary"
          onClick={onStartNewConversation}
        >
          + {t('newChat', userLanguage)}
        </button>
      </div>

      {/* Conversation Type Tabs */}
      <div className="conversation-type-tabs">
        {conversationTypes.map(type => (
          <button
            key={type.key}
            className={`conversation-type-tab ${activeTab === type.key ? 'active' : ''}`}
            onClick={() => setActiveTabLocal(type.key)}
          >
            <span className="tab-icon">{type.icon}</span>
            <span className="tab-label">{type.label}</span>
            {activeTab === type.key && (
              <span className="tab-count">({filteredConversations.length})</span>
            )}
          </button>
        ))}
      </div>

      {loadingConversations ? (
        <div className="loading">{t('loadingConversations', userLanguage)}</div>
      ) : filteredConversations.length > 0 ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '1.5rem'
        }}>
          {filteredConversations.map((conversation) => (
            <div
              key={conversation.id}
              onClick={() => {
                onSelectConversation(conversation);
                setActiveTab('chat');
              }}
              style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: currentConversation?.id === conversation.id ? '2px solid #667eea' : '1px solid #e2e8f0',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <h3 style={{ 
                  margin: 0, 
                  fontSize: '1.1rem', 
                  fontWeight: 600,
                  color: '#2d3748',
                  flex: 1
                }}>
                  {conversation.title || t('untitledConversation', userLanguage)}
                </h3>
                {conversation.conversation_type && conversation.conversation_type !== 'general' && (
                  <span className="conversation-type-badge" data-type={conversation.conversation_type}>
                    {conversationTypes.find(t => t.key === conversation.conversation_type)?.icon || '📌'}
                  </span>
                )}
              </div>
              {conversation.last_message_preview && (
                <p style={{ 
                  margin: '0.5rem 0', 
                  fontSize: '0.9rem', 
                  color: '#4a5568',
                  lineHeight: '1.4',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical'
                }}>
                  {conversation.last_message_preview}
                </p>
              )}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginTop: '0.75rem',
                paddingTop: '0.75rem',
                borderTop: '1px solid #e2e8f0'
              }}>
                <p style={{ 
                  margin: 0, 
                  fontSize: '0.85rem', 
                  color: '#718096' 
                }}>
                  {new Date(conversation.created_at).toLocaleDateString(dateLocale, {
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </p>
                {conversation.message_count !== undefined && (
                  <span style={{
                    fontSize: '0.75rem',
                    color: '#667eea',
                    background: 'rgba(102, 126, 234, 0.1)',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '12px',
                    fontWeight: 600
                  }}>
                    {conversation.message_count} {conversation.message_count === 1 ? t('messageSingular', userLanguage) : t('messagePlural', userLanguage)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <p>{t('noConversationsYet', userLanguage)}</p>
          <button
            className="btn"
            onClick={onStartNewConversation}
          >
            {t('startNewChat', userLanguage)}
          </button>
        </div>
      )}
    </div>
  );
};

export default ConversationsTab;

